import moment from 'moment';
import ReactQuill from 'react-quill';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import AppBar from '@material-ui/core/AppBar';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import 'react-accessible-accordion/dist/fancy-example.css';
import { TextField, CircularProgress } from '@mui/material';

import CreateRelation from './prop/CreateRelation';
import { getIntegrationStatus } from '../../store/Integration';
import ConnectionStatus from '../Integration/ConnectionStatus';
import IntegrationSelector from '../Integration/IntegrationSelector';
import { sendCommunication, getWhatsAppTemplates } from '../../store/Activities';

const RightSidebar = ({ setIsRightSidebar, singlePageData, objectType }) => {
  const dispatch = useDispatch();

  const chatBoxRef = useRef(null);
  let { currentWorkspace } = useSelector(state => state['Settings']);

  // Get conversation state from Redux
  const { associations, conversation, whatsappTemplates } = useSelector(state => state['Activities']);
  const integrationStatus = useSelector(state => state['Integration']['integrationStatus']);
  const { grouped, loading, sending } = conversation;

  // WhatsApp templates
  const { templates: waTemplates, loading: templatesLoading } = whatsappTemplates || {};

  // Twilio (SMS) connection status
  const twilioStatus = integrationStatus?.['twilio'];
  const isCheckingTwilio = twilioStatus?.['loading'] ?? true;
  const hasTwilioConnection = twilioStatus?.['data']?.['isConnected'] ?? false;

  // WhatsApp connection status
  const whatsappStatus = integrationStatus?.['whatsapp'];
  const isCheckingWhatsApp = whatsappStatus?.['loading'] ?? true;
  const hasWhatsAppConnection = whatsappStatus?.['data']?.['isConnected'] ?? false;

  // Local state for form inputs
  let [selectedTab, setSelectedTab] = useState(0);
  let [messageText, setMessageText] = useState('');
  let [showMoreDropdown, setShowMoreDropdown] = useState(false);
  let [selectedIntegrationId, setSelectedIntegrationId] = useState('');
  let [selectedWhatsAppIntegrationId, setSelectedWhatsAppIntegrationId] = useState('');

  // WhatsApp template state (consolidated)
  let [waTemplateState, setWaTemplateState] = useState({
    mode: 'text', // 'text' | 'template'
    selected: null, // selected template object
    variables: {} // { '1': 'value1', '2': 'value2' } for template variables
  });

  // ReactQuill editor config
  const editorModules = { toolbar: '#chat-toolbar', clipboard: { matchVisual: false } };
  const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align'];

  // Toggle dropdown
  const toggleDropdown = () => setShowMoreDropdown(prev => !prev);

  // Character count
  const messageLength = messageText.replace(/<[^>]*>/g, '').length; // Strip HTML tags for count
  const remainingChars = 160 - (messageLength % 160);
  const segments = Math.ceil(messageLength / 160) || 1;

  // Get contact info from singlePageData
  const contactId = singlePageData?.['_id'] || singlePageData?.['contacts']?.[0]?.['_id'] || '';
  const contactPhone = singlePageData?.['phone'] || singlePageData?.['contacts']?.[0]?.['phone'] || '';
  const contactEmail = singlePageData?.['email'] || singlePageData?.['contacts']?.[0]?.['email'] || '';
  const contactName = singlePageData?.['firstName'] ? `${singlePageData['firstName']} ${singlePageData['lastName'] || ''}`.trim()
                    : singlePageData?.contacts?.[0]?.['firstName'] ? `${singlePageData['contacts'][0]['firstName']} ${singlePageData['contacts'][0]['lastName'] || ''}`.trim() : 'Contact';

  // Check Twilio and WhatsApp connection on mount
  useEffect(() => {
    dispatch(getIntegrationStatus('Twilio'));
    dispatch(getIntegrationStatus('WhatsApp'));
  }, [dispatch]);

  // Fetch conversation history when connected and has contactPhone
  useEffect(() => {
    if ((hasTwilioConnection || hasWhatsAppConnection) && contactPhone) {
      dispatch({ type: 'GET_CONVERSATION_HISTORY', payload: { identifier: contactPhone, channels: 'sms,whatsapp,email' } });
    }
  }, [hasTwilioConnection, hasWhatsAppConnection, contactPhone, dispatch]);

  // Fetch WhatsApp templates when connected and in template mode
  useEffect(() => {
    if (hasWhatsAppConnection && waTemplateState['mode'] === 'template' && (!waTemplates || waTemplates.length === 0)) {
      dispatch(getWhatsAppTemplates());
    }
  }, [hasWhatsAppConnection, waTemplateState['mode'], waTemplates, dispatch]);

  // Get messages for current tab
  const smsMessages = grouped?.['sms'] || [];
  const whatsappMessages = grouped?.['whatsapp'] || [];

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatBoxRef['current']) chatBoxRef['current']['scrollTop'] = chatBoxRef['current']['scrollHeight'];
  }, [smsMessages, whatsappMessages]);

  // Escape key handler removed - was causing sidebar to close unexpectedly
  // TODO: Re-enable with proper focus management if needed

  const handleSendSMS = () => {
    if (!messageText.trim() || !contactPhone) return;

    // Build associations object from Redux state, fallback to contactId from props
    const smsAssociations = { ...associations };
    if (!smsAssociations['contacts']?.length && contactId) smsAssociations['contacts'] = [contactId];

    dispatch(sendCommunication({
      channel: 'sms',
      to: contactPhone,
      associations: smsAssociations,
      integrationId: selectedIntegrationId || undefined,
      message: messageText.replace(/<[^>]*>/g, '').trim(),
      successCallback: () => setMessageText('')
    }));
  };

  const handleSendWhatsApp = () => {
    if (!contactPhone) return;

    // Build associations object from Redux state, fallback to contactId from props
    const waAssociations = { ...associations };
    if (!waAssociations['contacts']?.length && contactId) waAssociations['contacts'] = [contactId];

    // Template mode
    if (waTemplateState['mode'] === 'template') {
      if (!waTemplateState['selected']) return;
      const vars = templateHelpers.getVariables(waTemplateState['selected']);
      const allVarsFilled = vars.every(v => waTemplateState['variables'][v]?.trim());
      if (vars.length > 0 && !allVarsFilled) return;

      // Build template components with body parameters (matching Meta API format)
      const templateParams = vars.length > 0 ? [{
        type: 'body',
        parameters: vars.map(v => ({ type: 'text', text: waTemplateState['variables'][v] }))
      }] : [];

      dispatch(sendCommunication({
        channel: 'whatsapp',
        messageType: 'template',

        templateParams,
        to: contactPhone,
        associations: waAssociations,
        integrationId: selectedWhatsAppIntegrationId || undefined,
        templateName: waTemplateState['selected']['name'],
        successCallback: () => setWaTemplateState({ mode: 'text', selected: null, variables: {} })
      }));
      return;
    }

    // Text mode - only allowed if 24-hour window is open
    if (!messageText.trim()) return;

    // Check 24-hour window before sending text message
    const recentInbound = whatsappMessages.some(msg => {
      if (msg['direction'] !== 'inbound') return false;
      const msgTime = moment(msg['timestamp'] || msg['createdAt']);
      return moment().diff(msgTime, 'hours') < 24;
    });

    if (!recentInbound) {
      // This shouldn't happen since UI is disabled, but safety check
      alert('Cannot send text message: 24-hour messaging window is closed. Please use a template message.');
      return;
    }

    dispatch(sendCommunication({
      channel: 'whatsapp',
      to: contactPhone,
      associations: waAssociations,
      integrationId: selectedWhatsAppIntegrationId || undefined,
      message: messageText.replace(/<[^>]*>/g, '').trim(),
      successCallback: () => setMessageText('')
    }));
  };

  const handleKeyPress = (e) => {
    // Send message on Ctrl+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (selectedTab === 0) handleSendSMS();
      else if (selectedTab === 1) handleSendWhatsApp();
    }
  };

  const formatTime = (timestamp) => { return moment(timestamp).format('h:mm a') }

  const formatDate = (timestamp) => {
    const date = moment(timestamp);
    if (date.isSame(moment(), 'day')) return 'Today';
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return date.format('MMMM Do, YYYY');
  };

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const date = moment(msg.timestamp || msg.createdAt).format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const groupedSMSMessages = groupMessagesByDate(smsMessages);
  const groupedWhatsAppMessages = groupMessagesByDate(whatsappMessages);

  // WhatsApp character limits
  const waMessageLength = messageText.replace(/<[^>]*>/g, '').length;
  const waRemainingChars = 4096 - waMessageLength;

  // WhatsApp template helpers
  const templateHelpers = {
    // Check if template requires media/header parameters (carousel, image, video, document headers)
    requiresMedia: (template) => {
      if (!template?.['components']) return false;
      const header = template['components'].find(c => c['type'] === 'HEADER');
      if (!header) return false;
      // Header formats that require media upload
      const mediaFormats = ['IMAGE', 'VIDEO', 'DOCUMENT'];
      return mediaFormats.includes(header['format']);
    },
    // Check if template is a carousel (has CAROUSEL component)
    isCarousel: (template) => {
      if (!template?.['components']) return false;
      return template['components'].some(c => c['type'] === 'CAROUSEL');
    },
    // Check if template can be sent with text-only parameters
    isTextOnly: (template) => {
      return !templateHelpers.requiresMedia(template) && !templateHelpers.isCarousel(template);
    },
    // Extract variable numbers from template (e.g., ['1', '2'])
    getVariables: (template) => {
      if (!template?.['components']) return [];
      const vars = [];
      template['components'].forEach(comp => {
        if (comp['type'] === 'BODY' && comp['text']) {
          (comp['text'].match(/\{\{(\d+)\}\}/g) || []).forEach(m => {
            const v = m.replace(/[{}]/g, '');
            if (!vars.includes(v)) vars.push(v);
          });
        }
      });
      return vars.sort((a, b) => parseInt(a) - parseInt(b));
    },
    // Generate preview with variables replaced
    getPreview: (template, variables) => {
      const body = template?.['components']?.find(c => c['type'] === 'BODY')?.['text'] || '';
      return Object.keys(variables).reduce((txt, k) => txt.replace(`{{${k}}}`, variables[k] || `[Variable ${k}]`), body);
    },
    // Handle template selection - initializes variables
    select: (template) => {
      const vars = templateHelpers.getVariables(template);
      setWaTemplateState({ ...waTemplateState, selected: template, variables: vars.reduce((acc, v) => ({ ...acc, [v]: '' }), {}) });
    },
    // Update a single variable value
    updateVariable: (varNum, value) => {
      setWaTemplateState({ ...waTemplateState, variables: { ...waTemplateState['variables'], [varNum]: value } });
    }
  };

  // Filter templates to only show text-only templates (no media/carousel requirements)
  const availableTemplates = (waTemplates || []).filter(t => templateHelpers.isTextOnly(t));
  const mediaTemplatesCount = (waTemplates || []).length - availableTemplates.length;

  // Check if WhatsApp 24-hour messaging window is open (has inbound message within last 24 hours)
  const hasRecentInbound = whatsappMessages.some(msg => {
    if (msg['direction'] !== 'inbound') return false;
    const msgTime = moment(msg['timestamp'] || msg['createdAt']);
    const hoursSinceMsg = moment().diff(msgTime, 'hours');
    return hoursSinceMsg < 24;
  });

  // Get time remaining in 24-hour window (for display)
  const getWindowTimeRemaining = () => {
    const lastInbound = whatsappMessages
      .filter(msg => msg['direction'] === 'inbound')
      .sort((a, b) => moment(b['timestamp'] || b['createdAt']).diff(moment(a['timestamp'] || a['createdAt'])))[0];
    if (!lastInbound) return null;
    const msgTime = moment(lastInbound['timestamp'] || lastInbound['createdAt']);
    const windowEnd = msgTime.add(24, 'hours');
    const hoursRemaining = windowEnd.diff(moment(), 'hours');
    const minutesRemaining = windowEnd.diff(moment(), 'minutes') % 60;
    if (hoursRemaining > 0) return `${hoursRemaining}h ${minutesRemaining}m`;
    if (minutesRemaining > 0) return `${minutesRemaining}m`;
    return null;
  };

  // Loading state while checking connection
  if (isCheckingTwilio && isCheckingWhatsApp) {
    return (
      <aside className="deal-sidebar style-right-sidebar">
        <div className='top-massage-area'>
          <div className='header-chatbox'>
            <div className='left-title-area'>
              <h3>Communication</h3>
            </div>
            <div className='right-area comm-header-actions'>
              <button className='massage-box' onClick={() => setIsRightSidebar(false)} title="Close">
                <i className='icon'>×</i>
              </button>
            </div>
          </div>
          <div className='comm-loading'>
            <CircularProgress size={24} />
            <span className='comm-loading-text'>Checking connections...</span>
          </div>
        </div>
      </aside>
    );
  }

  // Render chat message list
  const renderMessageList = (groupedMsgs, channelMessages) => {
    if (loading) return <div className='comm-loading'><CircularProgress size={30} /></div>;
    if (channelMessages.length === 0) return <div className='comm-empty-messages'><p>No messages yet. Start the conversation!</p></div>;

    // Get sender phone from message (supports both SMS from field and WhatsApp metadata)
    const getSenderPhone = (msg) => msg['from'] || msg['metadata']?.['phoneNumber'] || msg['metadata']?.['verifiedName'];

    return Object.keys(groupedMsgs).map(date => (
      <div key={date}>
        <div className='date-chat'><h5>{formatDate(date)}</h5></div>
        <div className='chat-box-inner'>
          {groupedMsgs[date].map((msg, index) => (
            <div key={msg['_id'] || index} className={`chat-box ${msg['direction'] === 'inbound' ? 'assistant' : 'user'}`}>
              <div className='chat-box-text'>{msg['message'] || msg['content'] || msg['body'] || msg['description'] || msg['metadata']?.['body']}</div>
              <span className='chat-time'>
                {formatTime(msg['timestamp'] || msg['createdAt'])}
                {getSenderPhone(msg) && msg['direction'] === 'outbound' && <span className='chat-from'> · From: {getSenderPhone(msg)}</span>}
                {msg['to'] && msg['direction'] === 'inbound' && <span className='chat-from'> · To: {msg['to']}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  // Show chat UI
  return (
    <aside className="deal-sidebar style-right-sidebar">
      <div className='top-massage-area'>
        <div className='header-chatbox'>
          <div className='left-title-area'><h3>Communication</h3></div>
          <div className='right-area comm-header-actions'>
            <button className='massage-box' onClick={() => dispatch({ type: 'GET_CONVERSATION_HISTORY', payload: { identifier: contactPhone, channels: 'sms,whatsapp,email' } })} title="Refresh messages">
              <i className='icon'>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M21 3V8H16" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </i>
            </button>
            <button className='massage-box' style={{fontSize: '18px'}} onClick={() => setIsRightSidebar(false)} title="Close"><i className='icon'>×</i></button>
          </div>
        </div>
        <div className='user-area comm-user-area'>
          <div className='left-area'>
            <div className='img-box'>
              <img src={require('../../static/img/user-img-1.png')} alt='' />
            </div>
            <div>
              <p className='comm-contact-name'>{contactName}</p>
              <small className='comm-contact-detail'>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className='comm-contact-icon'>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                {contactPhone}
              </small>
              {contactEmail && <small className='comm-contact-detail'>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className='comm-contact-icon'>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {contactEmail}
              </small>}
            </div>
          </div>
        </div>
        <div className='chat-box-area' ref={chatBoxRef}>
          {selectedTab === 0 && renderMessageList(groupedSMSMessages, smsMessages)}
          {selectedTab === 1 && renderMessageList(groupedWhatsAppMessages, whatsappMessages)}
        </div>
      </div>
      <div className='bottom-massage-box-area'>
        <div className='tabs-area'>
          <div className='left-area'>
            <AppBar position="static">
              <Tabs value={selectedTab} onChange={(event, newValue) => setSelectedTab(newValue)} aria-label="communication tabs">
                <Tab label="SMS" />
                <Tab label="WhatsApp" />
                <Tab label="Email" disabled />
              </Tabs>
            </AppBar>
          </div>
        </div>
        <div className='tabs-content'>
          <TabPanel value={selectedTab} index={0}>
            {!hasTwilioConnection ? (
              <div className='comm-connect-box'>
                <div className='comm-connect-content'>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="#4B5563" />
                    <path d="M7 7H17V9H7V7ZM7 11H17V13H7V11Z" fill="#4B5563" />
                  </svg>
                  <h4>Connect Twilio to Send SMS</h4>
                  <p>Connect Twilio to send SMS messages directly from your CRM.</p>
                </div>
                <ConnectionStatus toolName="Twilio" compact={true} onConnect={() => dispatch(getIntegrationStatus('Twilio'))} />
              </div>
            ) : (
              <div className='from-massage'>
                <div className='top-from-to'>
                  <div className="chat-container">
                    <div className="chat-row">
                      <div className="chat-input-group comm-input-full">
                        <label className="chat-label">From:</label>
                        <IntegrationSelector toolName="Twilio" value={selectedIntegrationId} onChange={setSelectedIntegrationId} placeholder="Select Twilio account" />
                      </div>
                    </div>
                    <div className="chat-row comm-row-send">
                      <div className="chat-input-group comm-input-flex">
                        <label className="chat-label">To:</label>
                        <input value={contactPhone} readOnly className="chat-to-input" />
                      </div>
                      {messageText.replace(/<[^>]*>/g, '').trim() && (
                        <button onClick={handleSendSMS} className='btn-style-send' disabled={sending || !contactPhone}>
                          {sending ? <CircularProgress size={14} color="inherit" /> : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="editor-wrapper comm-editor-wrap" onKeyDown={handleKeyPress}>
                      <ReactQuill theme="snow" formats={formats} readOnly={sending} value={messageText} modules={editorModules} bounds=".editor-wrapper" placeholder="Type your message... (Ctrl+Enter to send)" onChange={(value) => { const plainText = value.replace(/<[^>]*>/g, ''); if (plainText.length <= 1600) setMessageText(value); }} />
                      <div id="chat-toolbar" className="bottom-toolbar comm-toolbar-margin">
                        <span className="ql-formats">
                          <button className="ql-bold" />
                          <button className="ql-italic" />
                          <button className="ql-underline" />
                          <button className="ql-strike" />
                        </span>
                        <div className="ql-formats more-dropdown">
                          <button className="more-toggle" onClick={toggleDropdown}>More ▾</button>
                          <span className={`more-dropdown-content ${showMoreDropdown ? 'comm-dropdown-show' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" className="comm-dropdown-arrow"><path clipPath="url(#:ra5:)" fill="none" stroke="#CBD6E2" strokeWidth="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                            <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                              <select className="ql-font" />
                              <select className="ql-size" />
                              <select className="ql-color" />
                              <select className="ql-background" />
                              <button className="ql-blockquote" />
                              <button className="ql-list" value="ordered" />
                              <select className="ql-align" />
                            </span>
                          </span>
                        </div>
                        <span className="ql-formats">
                          <button className="ql-list" value="ordered" />
                          <button className="ql-list" value="bullet" />
                        </span>
                        <span className="ql-formats">
                          <button className="ql-link" />
                          <button className="ql-image" />
                        </span>
                      </div>
                      <div className="comm-char-count">
                        <span>{messageLength}/1600 characters</span>
                        <span>{segments} segment{segments !== 1 ? 's' : ''} ({remainingChars} chars left)</span>
                      </div>
                    </div>
                    <div className="add-note-sec inline-relation">
                      <div className="add-note-box">
                        <div className="bottom-note-box">
                          <CreateRelation type="new" initialAssociations={associations} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            {!hasWhatsAppConnection ? (
              <div className='from-massage'>
                <div className='top-from-to'>
                  <div className="chat-container">
                    <div className='whatsapp-box comm-whatsapp-box'>
                      <div className='left-area'>
                        <h4>Reach out to your customer on their favourite messaging app</h4>
                        <p>Stay closer to your customers by providing instant support, sending timely updates, and creating engaging interactions.</p>
                      </div>
                      <div className='right-area comm-whatsapp-connect'>
                        <ConnectionStatus toolName="WhatsApp" compact={true} onConnect={() => dispatch(getIntegrationStatus('WhatsApp'))} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='from-massage'>
                <div className='top-from-to'>
                  <div className="chat-container">
                    <div className="chat-row">
                      <div className="chat-input-group comm-input-full">
                        <label className="chat-label">From:</label>
                        <IntegrationSelector toolName="WhatsApp" value={selectedWhatsAppIntegrationId} onChange={setSelectedWhatsAppIntegrationId} placeholder="Select WhatsApp account" />
                      </div>
                    </div>
                    <div className="chat-row comm-row-send">
                      <div className="chat-input-group comm-input-flex">
                        <label className="chat-label">To:</label>
                        <input value={contactPhone} readOnly className="chat-to-input" />
                      </div>
                      {((waTemplateState['mode'] === 'text' && messageText.replace(/<[^>]*>/g, '').trim() && hasRecentInbound) ||
                        (waTemplateState['mode'] === 'template' && waTemplateState['selected'])) && (
                        <button onClick={handleSendWhatsApp} className='btn-style-send' disabled={sending || !contactPhone || (waTemplateState['mode'] === 'text' && !hasRecentInbound)}>
                          {sending ? <CircularProgress size={14} color="inherit" /> : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Message Mode Toggle */}
                    <div className="chat-row" style={{ marginBottom: '10px' }}>
                      <div className="wa-mode-toggle">
                        <button
                          className={`wa-mode-btn ${waTemplateState['mode'] === 'text' ? 'active' : ''}`}
                          onClick={() => setWaTemplateState({ ...waTemplateState, mode: 'text' })}
                        >
                          Text Message
                        </button>
                        <button
                          className={`wa-mode-btn ${waTemplateState['mode'] === 'template' ? 'active' : ''}`}
                          onClick={() => setWaTemplateState({ ...waTemplateState, mode: 'template' })}
                        >
                          Template
                        </button>
                      </div>
                    </div>

                    {/* Text Message Mode */}
                    {waTemplateState['mode'] === 'text' && (
                      <>
                        {/* 24-hour window status indicator */}
                        {hasRecentInbound ? (
                          <div className="wa-window-status wa-window-open" style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#166534', fontSize: '14px' }}>✓</span>
                            <span style={{ color: '#166534', fontSize: '12px' }}>
                              24-hour window open {getWindowTimeRemaining() && `(${getWindowTimeRemaining()} remaining)`}
                            </span>
                          </div>
                        ) : (
                          <div className="wa-window-status wa-window-closed" style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ color: '#92400e', fontSize: '14px' }}>⚠</span>
                              <span style={{ color: '#92400e', fontSize: '12px', fontWeight: 500 }}>24-hour messaging window closed</span>
                            </div>
                            <p style={{ color: '#92400e', fontSize: '11px', margin: 0 }}>
                              Contact hasn't replied in 24 hours. Use a template message for initial outreach, or wait for them to reply.
                            </p>
                          </div>
                        )}

                        <div className="editor-wrapper comm-editor-wrap" onKeyDown={handleKeyPress} style={{ opacity: hasRecentInbound ? 1 : 0.6 }}>
                          <ReactQuill
                            theme="snow"
                            formats={formats}
                            readOnly={sending || !hasRecentInbound}
                            value={messageText}
                            modules={editorModules}
                            bounds=".editor-wrapper"
                            placeholder={hasRecentInbound ? "Type your WhatsApp message... (Ctrl+Enter to send)" : "Text messages disabled - use Template mode"}
                            onChange={(value) => { if (hasRecentInbound) { const plainText = value.replace(/<[^>]*>/g, ''); if (plainText.length <= 4096) setMessageText(value); }}}
                          />
                          <div id="chat-toolbar" className="bottom-toolbar comm-toolbar-margin">
                            <span className="ql-formats">
                              <button className="ql-bold" disabled={!hasRecentInbound} />
                              <button className="ql-italic" disabled={!hasRecentInbound} />
                              <button className="ql-underline" disabled={!hasRecentInbound} />
                              <button className="ql-strike" disabled={!hasRecentInbound} />
                            </span>
                            <span className="ql-formats">
                              <button className="ql-list" value="ordered" disabled={!hasRecentInbound} />
                              <button className="ql-list" value="bullet" disabled={!hasRecentInbound} />
                            </span>
                            <span className="ql-formats">
                              <button className="ql-link" disabled={!hasRecentInbound} />
                            </span>
                          </div>
                          <div className="comm-char-count">
                            <span>{waMessageLength}/4096 characters</span>
                            <span>{waRemainingChars} chars remaining</span>
                          </div>
                        </div>

                        {!hasRecentInbound && (
                          <button
                            onClick={() => setWaTemplateState({ ...waTemplateState, mode: 'template' })}
                            className="btn-style-new"
                            style={{ width: '100%', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Switch to Template Mode
                          </button>
                        )}
                      </>
                    )}

                    {/* Template Message Mode */}
                    {waTemplateState['mode'] === 'template' && (
                      <div className="wa-template-section">
                        {templatesLoading ? (
                          <div className="comm-loading" style={{ padding: '20px' }}>
                            <CircularProgress size={20} />
                            <span style={{ marginLeft: '8px' }}>Loading templates...</span>
                          </div>
                        ) : (
                          <>
                            {/* Template Selector */}
                            <div className="chat-input-group" style={{ marginBottom: '12px' }}>
                              <label className="chat-label">Template:</label>
                              <select
                                className="chat-to-input"
                                value={waTemplateState['selected']?.['name'] || ''}
                                onChange={(e) => {
                                  const template = availableTemplates.find(t => t['name'] === e.target.value);
                                  if (template) templateHelpers.select(template);
                                  else setWaTemplateState({ ...waTemplateState, selected: null, variables: {} });
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <option value="">Select a template...</option>
                                {availableTemplates.map(t => (
                                  <option key={t['name']} value={t['name']}>
                                    {t['name']} ({t['language']}) - {t['category']}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Note about media templates */}
                            {mediaTemplatesCount > 0 && (
                              <p style={{ color: '#9ca3af', fontSize: '11px', margin: '0 0 12px 0' }}>
                                {mediaTemplatesCount} template{mediaTemplatesCount > 1 ? 's' : ''} hidden (require media upload)
                              </p>
                            )}

                            {/* Template Variables */}
                            {waTemplateState['selected'] && templateHelpers.getVariables(waTemplateState['selected']).length > 0 && (
                              <div className="wa-template-vars" style={{ marginBottom: '12px' }}>
                                <label className="chat-label" style={{ marginBottom: '8px', display: 'block' }}>Variables:</label>
                                {templateHelpers.getVariables(waTemplateState['selected']).map(varNum => (
                                  <div key={varNum} style={{ marginBottom: '8px' }}>
                                    <input
                                      type="text"
                                      className="chat-to-input"
                                      placeholder={`Variable {{${varNum}}}`}
                                      value={waTemplateState['variables'][varNum] || ''}
                                      onChange={(e) => templateHelpers.updateVariable(varNum, e.target.value)}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Template Preview */}
                            {waTemplateState['selected'] && (
                              <div className="wa-template-preview" style={{ background: '#f3f4f6', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                                <label className="chat-label" style={{ marginBottom: '6px', display: 'block', fontSize: '11px' }}>Preview:</label>
                                <p style={{ margin: 0, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                                  {templateHelpers.getPreview(waTemplateState['selected'], waTemplateState['variables'])}
                                </p>
                              </div>
                            )}

                            {availableTemplates.length === 0 && (
                              <p style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                                {(waTemplates || []).length > 0
                                  ? 'No text-only templates available. All your templates require media upload.'
                                  : 'No approved templates found. Create templates in Meta Business Suite.'}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <div className="add-note-sec inline-relation">
                      <div className="add-note-box">
                        <div className="bottom-note-box">
                          <CreateRelation type="new" initialAssociations={associations} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <div className='from-massage'>
              <div className='top-from-to'>
                <div className="chat-container">
                  <p className='comm-empty-messages'>Email functionality coming soon</p>
                </div>
              </div>
            </div>
          </TabPanel>
        </div>
      </div>
    </aside>
  )
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other} >
      {value === index && <Box p={0}> <Typography component="div">{children}</Typography> </Box>}
    </div>
  )
}

export default RightSidebar;
